// src/components/Navigation/Sidebar.js
import React, { useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGlobal } from '../../context/GlobalContext';
import './Sidebar.css';

export function Sidebar() {
  const { state, dispatch } = useGlobal();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const firstFocusableRef = useRef(null);

  const closeSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, [dispatch]);

  // Handle Escape key to close sidebar
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && state.sidebarOpen) {
        closeSidebar();
      }
    };

    if (state.sidebarOpen) {
      document.addEventListener('keydown', handleEscape);
      // Focus first element when sidebar opens
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [state.sidebarOpen, closeSidebar]);

  // Handle keyboard events on overlay
  const handleOverlayKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closeSidebar();
    }
  };

  const menuItems = [
    {
      section: 'Learning Resources',
      items: [
        { path: '/ai-fluency', label: 'AI Fluency Initiative', icon: '🎓' },
        { path: '/ethics', label: 'AI Ethics & Responsible Use', icon: '⚖️' },
        { path: '/agentic-ai', label: 'Agentic AI', icon: '🤖' },
        { path: '/prompt-engineering', label: 'Prompt Engineering Tips', icon: '🛠️' },
        { path: '/prompt-library', label: 'Prompt Library', icon: '🏛️' },
        { path: '/podcasts', label: 'AI-Generated Podcasts', icon: '🎧' },
        { path: '/newsletter', label: 'Newsletter', icon: '📧' }
      ]
    },
    {
      section: 'Quick Links',
      items: [
        { path: '/', label: 'Home', icon: '🏠' },
        { path: 'https://app.supplychainbrutus.com', label: 'Launch Application', icon: '🚀', external: true }
      ]
    }
  ];

  return (
    <>
      <nav
        id="sidebar-navigation"
        ref={sidebarRef}
        className={`sidebar ${state.sidebarOpen ? 'active' : ''}`}
        aria-label="Main navigation"
        aria-hidden={!state.sidebarOpen}
      >
        <div className="sidebar-header">
          <div className="sidebar-title">Resources</div>
          <div className="sidebar-subtitle">Learn & Explore</div>
        </div>

        <div className="sidebar-menu">
          {menuItems.map((section, sectionIndex) => (
            <div key={section.section} className="menu-section">
              <h3>{section.section}</h3>
              {section.items.map((item, itemIndex) => (
                item.external ? (
                  <a
                    key={item.path}
                    ref={sectionIndex === 0 && itemIndex === 0 ? firstFocusableRef : null}
                    href={item.path}
                    className="menu-item"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeSidebar}
                    aria-label={`${item.label} (opens in new tab)`}
                  >
                    <span className="icon" aria-hidden="true">{item.icon}</span>
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    ref={sectionIndex === 0 && itemIndex === 0 ? firstFocusableRef : null}
                    to={item.path}
                    className={`menu-item ${
                      location.pathname === item.path ? 'active' : ''
                    }`}
                    onClick={closeSidebar}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                  >
                    <span className="icon" aria-hidden="true">{item.icon}</span>
                    {item.label}
                  </Link>
                )
              ))}
            </div>
          ))}
        </div>
      </nav>

      {state.sidebarOpen && (
        <div
          className="overlay"
          onClick={closeSidebar}
          onKeyDown={handleOverlayKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Close navigation menu"
        />
      )}
    </>
  );
}